// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

/**
 * True unless the action is a non-main payment-grid row call.
 *
 * Grid rows are deliberately concurrent — one call per cash-down row, each populating a different cell — so
 * everything that treats payments as a single ordered stream has to exclude them:
 * `paymentSequenceMiddleware`'s guard and `paymentRaceTelemetryMiddleware`'s out-of-order detection both
 * depend on exactly this rule, and a fix to it has to apply to both at once.
 *
 * Takes the TRIGGERING action, never a resolving one. `meta.paymentGrid` lives on the trigger, so a caller
 * holding a `PAYMENT_RECEIVED` must pass `action.meta.originalAction`; passing the resolving action instead
 * silently reports every grid row as a main payment.
 *
 * offer-redux keeps its own copies of this rule for the state it owns (`paymentReducer`,
 * `penciledInputsStateUtils`). Those are not reachable from here without widening that package's public
 * surface, so this is the dash-store side — shared so at least the two middlewares cannot drift apart.
 */
export const isMainPaymentTrigger = (triggerAction: AnyFSA | undefined): boolean => {
    const { gridAction = false, isMainPayment = false } = triggerAction?.meta?.paymentGrid || {};
    return !(gridAction && !isMainPayment);
};

/** `meta.originalAction` can be a bare string or absent, not just `{ type }` — reading `.type` off it unguarded can throw. */
export const getOriginalActionType = (action: AnyFSA | undefined): string | undefined => {
    const originalAction = action?.meta?.originalAction;
    return typeof originalAction === 'string' ? originalAction : originalAction?.type;
};
